/**
 * 会话管理器
 * 用于管理用户认证会话
 */

import { API_BASE_URL, AUTH_API } from '@config/api'
import { httpFetch } from '@/utils/httpClient'
import { ElMessage } from 'element-plus'
import i18n from '@/i18n'

const SESSION_REFRESH_INTERVAL_MS = 2 * 60 * 60 * 1000

class SessionManager {
    constructor() {
        this.SESSION_KEY = 'auth_session';
        this.REFRESH_KEY = 'auth_refresh';
        this.USER_KEY = 'auth_user';
        this.SESSION_ID_KEY = 'session_id';
        this.TOKEN_KEY = 'auth_token';
        this.sessionRefreshTimer = null;
    }

    /**
     * 设置会话信息
     * @param {Object} sessionData - 会话数据
     */
    async setSession(sessionData) {
        const { accessToken, refreshToken, user, expiresAt, sessionId, token } = sessionData;

        try {
            console.log('SessionManager - Setting session with data:', sessionData);

            // 主会话信息（短期）
            sessionStorage.setItem(this.SESSION_KEY, JSON.stringify({
                accessToken,
                user: this.obfuscateUserData(user),
                expiresAt,
                createdAt: Date.now()
            }));

            // 刷新令牌（更安全的方式存储）
            if (refreshToken) {
                await this.setRefreshToken(refreshToken);
            }

            // 存储完整用户信息到localStorage
            if (user) {
                localStorage.setItem(this.USER_KEY, JSON.stringify(user));
            }

            // 存储sessionId到localStorage
            if (sessionId) {
                localStorage.setItem(this.SESSION_ID_KEY, sessionId);
                console.log('SessionManager - Stored sessionId:', sessionId);
            } else {
                console.log('SessionManager - No sessionId provided');
            }

            const authToken = token || sessionId || accessToken
            if (authToken) {
                localStorage.setItem(this.TOKEN_KEY, authToken)
            }

            // 设置自动刷新，登录后立即续期一次
            this.setupTokenRefresh(expiresAt);
            this.startSessionRefreshTimer();
            await this.refreshSession({ notify: true, clearOnUnauthorized: false });
        } catch (error) {
            console.error('Failed to save session:', error);
            throw new Error('无法保存登录信息');
        }
    }

    /**
     * 获取当前会话
     * @returns {Object|null} 会话信息
     */
    async getSession() {
        try {
            const sessionStr = sessionStorage.getItem(this.SESSION_KEY);
            if (!sessionStr) return null;

            const session = JSON.parse(sessionStr);

            // 检查是否过期
            if (Date.now() > session.expiresAt) {
                await this.refreshToken();
                return this.getSession();
            }

            return session;
        } catch (error) {
            console.error('Failed to get session:', error);
            return null;
        }
    }

    /**
     * 获取sessionId
     * @returns {string|null} sessionId
     */
    getSessionId() {
        try {
            const sessionId = localStorage.getItem(this.SESSION_ID_KEY);
            console.log('SessionManager - Retrieved sessionId:', sessionId);
            return sessionId;
        } catch (error) {
            console.error('Failed to get session id:', error);
            return null;
        }
    }

    /**
     * 获取实时数据/WebSocket 使用的认证 token
     * @returns {string|null}
     */
    getAuthToken() {
        try {
            const token = localStorage.getItem(this.TOKEN_KEY)
            if (token) return token

            const sessionId = this.getSessionId()
            if (sessionId) return sessionId

            const sessionStr = sessionStorage.getItem(this.SESSION_KEY)
            if (sessionStr) {
                const session = JSON.parse(sessionStr)
                if (session?.accessToken) return session.accessToken
            }

            return null
        } catch (error) {
            console.error('Failed to get auth token:', error)
            return null
        }
    }

    /**
     * 检查是否有有效会话
     * @returns {Promise<boolean>} 是否有有效会话
     */
    async hasValidSession() {
        const session = await this.getSession();
        return !!session && Date.now() < session.expiresAt;
    }

    /**
     * 刷新访问令牌
     * @returns {Promise<boolean>} 刷新是否成功
     */
    async refreshToken() {
        try {
            const refreshToken = await this.getRefreshToken();
            if (!refreshToken) {
                this.clearSession();
                return false;
            }

            const response = await httpFetch(`${API_BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
            });

            if (response.ok) {
                const { accessToken, expiresIn } = await response.json();
                await this.updateAccessToken(accessToken, expiresIn);
                return true;
            } else {
                this.clearSession();
                return false;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.clearSession();
            return false;
        }
    }

    /**
     * 清除会话
     */
    clearSession() {
        console.log('SessionManager - Clearing session');
        this.stopSessionRefreshTimer();
        sessionStorage.removeItem(this.SESSION_KEY);
        localStorage.removeItem(this.REFRESH_KEY);
        localStorage.removeItem(this.USER_KEY);
        localStorage.removeItem(this.SESSION_ID_KEY);
        localStorage.removeItem(this.TOKEN_KEY);

        // 清除所有相关cookies
        document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.split('=');
            document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
    }

    /**
     * 设置刷新令牌（更安全的存储）
     * @param {string} token - 刷新令牌
     */
    async setRefreshToken(token) {
        try {
            // 简单示例：使用localStorage，实际应加密
            const encrypted = btoa(unescape(encodeURIComponent(token)));
            localStorage.setItem(this.REFRESH_KEY, encrypted);
        } catch (error) {
            console.error('Failed to save refresh token:', error);
        }
    }

    /**
     * 获取刷新令牌
     * @returns {Promise<string|null>} 刷新令牌
     */
    async getRefreshToken() {
        try {
            const encrypted = localStorage.getItem(this.REFRESH_KEY);
            if (!encrypted) return null;

            return decodeURIComponent(escape(atob(encrypted)));
        } catch (error) {
            console.error('Failed to get refresh token:', error);
            return null;
        }
    }

    /**
     * 混淆用户数据（减少敏感信息暴露）
     * @param {Object} user - 用户数据
     * @returns {Object} 混淆后的用户数据
     */
    obfuscateUserData(user) {
        if (!user) return null;

        return {
            id: user.id,
            username: user.username,
            email: this.maskEmail(user.email),
            roles: user.roles,
            privileges: user.privileges, // 添加privileges字段
            // 不存储敏感信息
        };
    }

    /**
     * 隐藏邮箱部分内容
     * @param {string} email - 邮箱地址
     * @returns {string} 隐藏部分的邮箱地址
     */
    maskEmail(email) {
        if (!email) return '';
        const [local, domain] = email.split('@');
        const maskedLocal = local.length > 3
            ? local.substring(0, 3) + '***'
            : '***';
        return `${maskedLocal}@${domain}`;
    }

    /**
     * 设置令牌自动刷新
     * @param {number} expiresAt - 过期时间戳
     */
    setupTokenRefresh(expiresAt) {
        // 在令牌过期前5分钟刷新
        const refreshTime = expiresAt - Date.now() - 5 * 60 * 1000;

        if (refreshTime > 0) {
            setTimeout(() => {
                this.refreshToken();
            }, refreshTime);
        }
    }

    /**
     * 调用 manage/user/refreshSession 续期 session（24h 有效，每 2h 刷新一次）
     * @param {{ notify?: boolean, clearOnUnauthorized?: boolean }} options
     * @returns {Promise<boolean>}
     */
    async refreshSession(options = {}) {
        const { notify = false, clearOnUnauthorized = true } = options
        const sessionId = this.getSessionId();
        if (!sessionId) {
            this.stopSessionRefreshTimer();
            return false;
        }

        try {
            const response = await httpFetch(AUTH_API.REFRESH_SESSION, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionId}`
                },
                body: JSON.stringify({})
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok && data.code === 200 && data.data?.sessionId) {
                const newSessionId = data.data.sessionId;
                localStorage.setItem(this.SESSION_ID_KEY, newSessionId);
                localStorage.setItem(this.TOKEN_KEY, newSessionId);
                console.log(
                    '[SessionManager] Session refreshed, valid for',
                    data.data.expiresInHours ?? 24,
                    'hours'
                );
                return true;
            }

            const failMessage = data.message || String(response.status);
            console.warn('[SessionManager] refreshSession failed:', failMessage);

            if (response.status === 401 || data.code === 401) {
                if (clearOnUnauthorized) {
                    this.clearSession();
                    if (typeof window !== 'undefined') {
                        ElMessage.warning(i18n.global.t('common.sessionExpired'));
                        if (process.env.NODE_ENV === 'production') {
                            window.location.hash = '#/';
                        } else {
                            window.location.href = process.env.BASE_URL || '/';
                        }
                    }
                } else if (notify) {
                    this.notifyRefreshFailed(failMessage);
                }
                return false;
            }

            if (notify) {
                this.notifyRefreshFailed(failMessage);
            }
            return false;
        } catch (error) {
            console.error('[SessionManager] refreshSession error:', error);
            if (notify) {
                this.notifyRefreshFailed(error.message);
            }
            return false;
        }
    }

    notifyRefreshFailed(serverMessage) {
        const fallback = i18n.global.t('login.sessionRefreshFailed');
        ElMessage.warning(serverMessage && serverMessage !== 'undefined' ? `${fallback}（${serverMessage}）` : fallback);
    }

    startSessionRefreshTimer() {
        this.stopSessionRefreshTimer();
        if (!this.getSessionId()) return;

        this.sessionRefreshTimer = setInterval(() => {
            this.refreshSession({ notify: true });
        }, SESSION_REFRESH_INTERVAL_MS);
    }

    stopSessionRefreshTimer() {
        if (this.sessionRefreshTimer) {
            clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = null;
        }
    }

    /** 应用启动时恢复定时刷新（已有登录态） */
    initSessionRefresh() {
        if (this.getSessionId()) {
            this.startSessionRefreshTimer();
        }
    }

    /**
     * 更新访问令牌
     * @param {string} accessToken - 新的访问令牌
     * @param {number} expiresIn - 过期时间（秒）
     */
    async updateAccessToken(accessToken, expiresIn) {
        const sessionStr = sessionStorage.getItem(this.SESSION_KEY);
        if (!sessionStr) return;

        const session = JSON.parse(sessionStr);
        session.accessToken = accessToken;
        session.expiresAt = Date.now() + (expiresIn * 1000);

        sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(session));

        // 重新设置刷新
        this.setupTokenRefresh(session.expiresAt);
    }

    /**
     * 获取认证头信息
     * @returns {Promise<string|null>} 认证头
     */
    async getAuthHeader() {
        const session = await this.getSession();
        return session ? `Bearer ${session.accessToken}` : null;
    }

    /**
     * 获取当前用户信息
     * @returns {Object|null} 用户信息
     */
    getCurrentUser() {
        try {
            const userStr = localStorage.getItem(this.USER_KEY);
            return userStr ? JSON.parse(userStr) : null;
        } catch (error) {
            console.error('Failed to get current user:', error);
            return null;
        }
    }

    /**
     * 获取用户权限级别
     * @returns {number} 权限级别 (0=管理员, 1=普通用户)
     */
    getUserPrivileges() {
        try {
            // 1. 尝试从 localStorage 获取 (完整用户信息)
            const user = this.getCurrentUser();
            if (user && user.privileges !== undefined) {
                return user.privileges;
            }

            // 2. 尝试从 sessionStorage 获取 (会话信息)
            const sessionStr = sessionStorage.getItem(this.SESSION_KEY);
            if (sessionStr) {
                const session = JSON.parse(sessionStr);
                if (session.user) {
                    if (session.user.privileges !== undefined) {
                        return session.user.privileges;
                    }
                    // Fallback check for roles in session
                    const roles = session.user.roles || session.user.role;
                    if (roles) {
                        const roleStr = String(roles).toLowerCase();
                        if (roleStr === 'admin' || roleStr.includes('admin')) {
                            return 0; // Admin
                        }
                    }
                }
            }

            // Fallback check for roles in local user
            if (user) {
                const roles = user.roles || user.role;
                if (roles) {
                    const roleStr = String(roles).toLowerCase();
                    if (roleStr === 'admin' || roleStr.includes('admin')) {
                        return 0; // Admin
                    }
                }
            }

            // 3. 默认返回普通用户权限 (1)，为了安全起见不默认为管理员
            console.warn('SessionManager - 无法获取用户权限，默认为普通用户(1)');
            return 1;
        } catch (error) {
            console.error('Failed to get user privileges:', error);
            // 出错时返回普通用户权限
            return 1;
        }
    }
}

// 导出单例实例
export default new SessionManager();